import { useEffect, useRef, useState } from "react";
import { API, errMsg, imgSrc, imageRatioClass, uploadFile, mergeHomeSections, normalizeBusinessSettings, prepareBusinessSettings, THEMES, BG_STYLES, LAYOUTS, BLOG_LAYOUTS, BLOG_DETAIL_LAYOUTS } from "@/lib/api";
import { toast } from "sonner";
import { Palette, FileText, LayoutList, Upload, ArrowUp, ArrowDown, Eye, EyeOff, LayoutTemplate, Plus, Trash2 } from "lucide-react";
import ImageRatioPicker from "@/components/ImageRatioPicker";
import VideoPlayer from "@/components/VideoPlayer";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

const CAT_DEFAULTS = [
  { key: "tali", title: "Tali Tambang PP", desc: "LUMBA LUMBA & KUDA EMAS — kuat, tahan cuaca, mengapung di air.", image: "" },
  { key: "katrol", title: "Katrol", desc: "Katrol LUMBA LUMBA dengan hook pengaman untuk angkat beban lebih ringan.", image: "" },
  { key: "timbangan", title: "Timbangan", desc: "Manual & digital dengan akurasi tinggi untuk toko hingga industri.", image: "" },
];
const BRAND_DEFAULTS = [
  { name: "LUMBA LUMBA", desc: "Tali PP & Katrol LUMBA LUMBA — Made in Indonesia, trusted quality untuk perkapalan, perikanan, dan industri.", image: "" },
  { name: "KUDA EMAS", desc: "Tali PP KUDA EMAS — lentur, kuat, dan ekonomis untuk kebutuhan pengikatan skala besar.", image: "" },
];
const FEATURE_DEFAULTS = [
  { title: "Supplier & Distributor Tunggal Resmi", desc: "Tunggal untuk Tali PP LUMBA LUMBA & KUDA EMAS, Katrol LUMBA LUMBA, dan Timbangan Manual & Digital." },
  { title: "Kualitas Sempurna", desc: "Setiap produk melewati seleksi kualitas ketat sebelum sampai ke tangan pelanggan." },
  { title: "Kepuasan Pelanggan", desc: "Prioritas kami adalah pelayanan responsif dan solusi terbaik untuk setiap kebutuhan Anda." },
  { title: "Seluruh Indonesia", desc: "Melayani pengiriman ke seluruh wilayah Indonesia." },
];

function ImgField({ label, value, onChange, ratio, onRatioChange, defaultRatio = "4:3", testid }) {
  const ref = useRef(null);
  const [up, setUp] = useState(false);
  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await API.post("/upload", fd);
      onChange(data.url);
      toast.success("Gambar terupload");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUp(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2 items-center">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls} placeholder="URL gambar atau upload →" />
        <button type="button" onClick={() => ref.current?.click()} disabled={up} data-testid={`${testid}-upload`} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-300 text-xs font-bold rounded-md hover:border-[#C9A227] hover:text-[#C9A227] transition-colors">
          <Upload className="h-4 w-4" /> {up ? "..." : "Upload"}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={doUpload} />
      </div>
      {value && (
        <div className={`w-36 max-w-full mt-2 rounded border border-slate-200 overflow-hidden bg-slate-100 ${imageRatioClass(ratio, defaultRatio)}`}>
          <img src={imgSrc(value)} alt={label} className="w-full h-full object-cover" />
        </div>
      )}
      {onRatioChange && <ImageRatioPicker value={ratio} onChange={onRatioChange} defaultRatio={defaultRatio} testid={`${testid}-ratio`} />}
    </div>
  );
}

function HeroSlideField({ slide, index, onChange, onRemove, onMove, isFirst, isLast }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      onChange({ ...slide, url: await uploadFile(file) });
      toast.success(slide.type === "video" ? "Video hero terupload" : "Gambar hero terupload");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid={`hero-slide-${index}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Slide {index + 1}</p>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className="p-1.5 text-slate-400 hover:text-[#0B1F3A] disabled:opacity-30" aria-label="Geser slide ke atas"><ArrowUp className="h-4 w-4" /></button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} className="p-1.5 text-slate-400 hover:text-[#0B1F3A] disabled:opacity-30" aria-label="Geser slide ke bawah"><ArrowDown className="h-4 w-4" /></button>
          <button type="button" onClick={onRemove} className="p-1.5 text-red-500 hover:text-red-700" aria-label="Hapus slide"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
        <select value={slide.type || "image"} onChange={(event) => onChange({ ...slide, type: event.target.value, url: "" })} className={inputCls}>
          <option value="image">Gambar</option>
          <option value="video">Video</option>
        </select>
        <div className="flex items-center gap-2">
          <input value={slide.url || ""} onChange={(event) => onChange({ ...slide, url: event.target.value })} className={inputCls} placeholder={slide.type === "video" ? "Link YouTube / URL video atau upload →" : "URL gambar atau upload →"} />
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2.5 text-xs font-bold"><Upload className="h-4 w-4" /> {uploading ? "..." : "Upload"}</button>
          <input ref={ref} type="file" accept={slide.type === "video" ? "video/mp4,video/webm,video/quicktime" : "image/*"} className="hidden" onChange={upload} />
        </div>
      </div>
      <input value={slide.alt || ""} onChange={(event) => onChange({ ...slide, alt: event.target.value })} className={inputCls} placeholder="Keterangan media (opsional)" />
      <div>
        <label className={labelCls}>Durasi slide (detik)</label>
        <input
          type="number"
          min="3"
          max="300"
          step="1"
          value={slide.duration_seconds ?? ""}
          onChange={(event) => onChange({ ...slide, duration_seconds: event.target.value })}
          className={inputCls}
          placeholder="12"
        />
        <p className="mt-1 text-[10px] text-slate-400">
          Semua media tampil 12 detik jika durasi dikosongkan. Rentang yang didukung 3–300 detik.
        </p>
      </div>
      {slide.url && (
        <div className="aspect-video w-full max-w-sm overflow-hidden rounded border border-slate-200 bg-black">
          {slide.type === "video" ? <VideoPlayer url={slide.url} title={slide.alt || `Preview slide ${index + 1}`} className="h-full w-full object-cover" /> : <img src={imgSrc(slide.url)} alt={slide.alt || `Preview slide ${index + 1}`} className="h-full w-full object-cover" />}
        </div>
      )}
      {slide.type === "video" && <p className="text-[10px] text-slate-400">Upload MP4/WebM/MOV maksimal 50MB, atau tempel link YouTube.</p>}
    </div>
  );
}

export default function Website() {
  const [tab, setTab] = useState("tema");
  const [s, setS] = useState(null);

  const load = () => API.get("/settings").then((r) => setS(normalizeBusinessSettings(r.data))).catch(() => toast.error("Gagal memuat pengaturan"));
  useEffect(() => { load(); }, []);

  if (!s) return <p className="text-slate-400">Memuat...</p>;

  const hc = s.home_content || {};
  const setHc = (k, v) => setS({ ...s, home_content: { ...hc, [k]: v } });
  const cats = hc.categories?.length ? hc.categories : CAT_DEFAULTS;
  const brands = hc.brands?.length ? hc.brands : BRAND_DEFAULTS;
  const feats = [0, 1, 2, 3].map((i) => ({ ...(hc.features?.[i] || FEATURE_DEFAULTS[i]) }));
  const layout = mergeHomeSections(s.home_sections || []);
  const heroSlides = hc.hero_slides?.length ? hc.hero_slides : (hc.hero_image ? [{ type: "image", url: hc.hero_image, alt: "" }] : []);
  const setHeroSlides = (slides) => setHc("hero_slides", slides);

  const setLayout = (arr) => setS({ ...s, home_sections: arr.map(({ key, label, visible }) => ({ key, label, visible })) });
  const move = (i, dir) => {
    const arr = [...layout];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLayout(arr);
  };

  const waNums = s.whatsapp_numbers || [];
  const igs = s.instagrams || [];
  const addrs = s.addresses || [];
  const banks = s.bank_accounts || [];
  const hours = s.operating_hours_list || [];

  const save = async () => {
    try {
      const payload = prepareBusinessSettings(s);
      await API.put("/settings", payload);
      setS(payload);
      window.dispatchEvent(new Event("nawadah:settings-updated"));
      toast.success("Pengaturan website disimpan — langsung terlihat di website setelah refresh");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const pickTheme = (v) => {
    setS({ ...s, theme: v });
    document.documentElement.setAttribute("data-theme", v);
  };

  const TABS = [
    { v: "tema", l: "Tema & Background", icon: Palette },
    { v: "desain", l: "Layout & Gaya", icon: LayoutTemplate },
    { v: "konten", l: "Konten & Gambar", icon: FileText },
    { v: "layout", l: "Tata Letak", icon: LayoutList },
  ];

  return (
    <div data-testid="admin-website">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Kelola Website</h1>
          <p className="text-sm text-slate-500">Ubah tema, konten, gambar, dan tata letak website promosi — tanpa coding</p>
        </div>
        <button onClick={save} data-testid="save-website" className="px-6 py-2.5 bg-gold bg-gold-hover text-[#0B1F3A] text-sm font-bold rounded-md transition-colors">
          Simpan & Terapkan
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6" data-testid="website-tabs">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`wtab-${t.v}`} className={`inline-flex items-center gap-2 px-5 py-2 text-xs font-bold rounded-full border transition-colors ${tab === t.v ? "bg-[#0B1F3A] text-white border-[#0B1F3A]" : "border-slate-300 text-slate-500 hover:border-[#0B1F3A]"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "tema" && (
        <div className="space-y-8">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold mb-1">Tema Warna Website</h2>
            <p className="text-xs text-slate-400 mb-5">Sekali pilih, seluruh halaman otomatis berubah selaras: background, tombol, teks, header, dan footer. Klik untuk pratinjau langsung, lalu Simpan & Terapkan.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="theme-grid">
              {THEMES.map((t) => (
                <button key={t.v} onClick={() => pickTheme(t.v)} data-testid={`theme-${t.v}`} className={`text-left border rounded-md p-4 transition-all ${s.theme === t.v ? "border-[#C9A227] ring-2 ring-[#C9A227]/40" : "border-slate-200 hover:border-slate-400"}`}>
                  {t.img ? (
                    <img src={t.img} alt={t.l} className="h-16 w-full object-cover rounded mb-3 border border-slate-200" />
                  ) : (
                    <div className="h-16 w-full rounded mb-3 border border-slate-200" style={{ background: `linear-gradient(120deg, ${t.c[0]} 0%, ${t.c[1]} 55%, ${t.c[2]} 135%)` }} />
                  )}
                  <p className="font-bold text-sm">{t.l}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{t.d}</p>
                  {s.theme === t.v && <p className="text-[10px] font-bold text-[#C9A227] mt-2 uppercase tracking-widest">Terpilih</p>}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold mb-1">Tekstur Background</h2>
            <p className="text-xs text-slate-400 mb-4">Lapisan tekstur di atas warna tema untuk bagian-bagian halaman utama.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bgstyle-grid">
              {BG_STYLES.map((b) => (
                <button key={b.v} onClick={() => setS({ ...s, bg_style: b.v })} data-testid={`bgstyle-${b.v}`} className={`text-left border rounded-md p-4 transition-colors ${s.bg_style === b.v ? "border-[#C9A227] bg-amber-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <p className="font-bold text-sm">{b.l}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{b.d}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "konten" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
            <h2 className="font-bold">Informasi Bisnis</h2>
            <p className="text-xs text-slate-400 -mt-3">Semua info di sini otomatis dipakai di seluruh website: navbar, footer, halaman kontak, tombol melayang, dan dokumen cetak.</p>
            <ImgField label="Logo Website (kosongkan = logo bawaan NAWADAH)" value={s.logo || ""} onChange={(v) => setS({ ...s, logo: v })} testid="site-logo" />
            <div>
              <ImgField label="Logo Sistem Bisnis (panel admin)" value={s.admin_logo || ""} onChange={(v) => setS({ ...s, admin_logo: v })} testid="admin-logo" />
              <p className="mt-2 text-xs text-slate-400">Gunakan gambar logo horizontal. Setelah disimpan, logo ini otomatis tampil di sidebar dan header panel admin. Kosongkan untuk kembali ke logo bawaan.</p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <div className="mb-2 flex items-center justify-between gap-3">
                <label className={labelCls + " mb-0"}>Jam Operasional (bisa lebih dari 1)</label>
                <button type="button" onClick={() => setS({ ...s, operating_hours_list: [...hours, { label: "", hours: "" }] })} data-testid="site-hours-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227]"><Plus className="h-3.5 w-3.5" /> Tambah Jam</button>
              </div>
              <div className="space-y-2">
                {hours.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 items-center gap-2">
                    <input value={item.label || ""} onChange={(event) => { const items = [...hours]; items[index] = { ...item, label: event.target.value }; setS({ ...s, operating_hours_list: items }); }} data-testid={`site-hours-label-${index}`} className={inputCls + " col-span-4"} placeholder="Senin–Sabtu" />
                    <input value={item.hours || ""} onChange={(event) => { const items = [...hours]; items[index] = { ...item, hours: event.target.value }; setS({ ...s, operating_hours_list: items }); }} data-testid={`site-hours-value-${index}`} className={inputCls + " col-span-7"} placeholder="08.00–16.00 WIB" />
                    <button type="button" onClick={() => setS({ ...s, operating_hours_list: hours.filter((_, itemIndex) => itemIndex !== index) })} aria-label={`Hapus jam ${index + 1}`} className="col-span-1 text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>Nomor WhatsApp (bisa lebih dari 1)</label>
                <button type="button" onClick={() => setS({ ...s, whatsapp_numbers: [...waNums, { label: `NDH-Admin ${waNums.length + 1}`, number: "", image: "" }] })} data-testid="wa-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227] hover:underline"><Plus className="h-3.5 w-3.5" /> Tambah Admin WhatsApp</button>
              </div>
              {waNums.length === 0 && <p className="text-xs text-slate-400">Belum ada daftar — sementara memakai nomor lama dari menu Pengaturan.</p>}
              <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">Nomor WhatsApp <strong>tidak ditampilkan</strong> di website — pengunjung hanya melihat label (mis: Admin Nawadah 1). Nomor asli hanya terlihat oleh Anda di sini.</p>
              <div className="mt-3 space-y-3" data-testid="wa-list">
                {waNums.map((w, i) => (
                  <div key={i} className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Admin WhatsApp {i + 1}</p>
                      <button type="button" onClick={() => setS({ ...s, whatsapp_numbers: waNums.filter((_, x) => x !== i) })} data-testid={`wa-remove-${i}`} className="inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /> Hapus</button>
                    </div>
                    <ImgField label="Foto / Ikon Admin (opsional)" value={w.image || ""} onChange={(value) => { const a = [...waNums]; a[i] = { ...w, image: value }; setS({ ...s, whatsapp_numbers: a }); }} defaultRatio="1:1" testid={`wa-image-${i}`} />
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>Nama yang tampil</label>
                        <input value={w.label || ""} onChange={(e) => { const a = [...waNums]; a[i] = { ...w, label: e.target.value }; setS({ ...s, whatsapp_numbers: a }); }} data-testid={`wa-label-${i}`} className={inputCls} placeholder="Contoh: NDH-Admin 1" />
                      </div>
                      <div>
                        <label className={labelCls}>Nomor WhatsApp</label>
                        <input value={w.number || ""} onChange={(e) => { const a = [...waNums]; a[i] = { ...w, number: e.target.value }; setS({ ...s, whatsapp_numbers: a }); }} data-testid={`wa-number-${i}`} className={inputCls} placeholder="08xx-xxxx-xxxx" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>Akun Instagram (bisa lebih dari 1)</label>
                <button type="button" onClick={() => setS({ ...s, instagrams: [...igs, { label: "", handle: "" }] })} data-testid="ig-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227] hover:underline"><Plus className="h-3.5 w-3.5" /> Tambah Akun</button>
              </div>
              {igs.length === 0 && <p className="text-xs text-slate-400">Belum ada daftar — sementara memakai akun lama dari menu Pengaturan.</p>}
              <div className="space-y-2" data-testid="ig-list">
                {igs.map((g, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={g.label || ""} onChange={(e) => { const a = [...igs]; a[i] = { ...g, label: e.target.value }; setS({ ...s, instagrams: a }); }} data-testid={`ig-label-${i}`} className={inputCls + " col-span-4"} placeholder="Label (mis: IG Pusat)" />
                    <input value={g.handle || ""} onChange={(e) => { const a = [...igs]; a[i] = { ...g, handle: e.target.value }; setS({ ...s, instagrams: a }); }} data-testid={`ig-handle-${i}`} className={inputCls + " col-span-7"} placeholder="@username" />
                    <button type="button" onClick={() => setS({ ...s, instagrams: igs.filter((_, x) => x !== i) })} data-testid={`ig-remove-${i}`} className="col-span-1 text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>Alamat / Lokasi (bisa lebih dari 1)</label>
                <button type="button" onClick={() => setS({ ...s, addresses: [...addrs, { label: "", address: "" }] })} data-testid="addr-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227] hover:underline"><Plus className="h-3.5 w-3.5" /> Tambah Alamat</button>
              </div>
              {addrs.length === 0 && <p className="text-xs text-slate-400">Belum ada daftar — lokasi tampil sebagai "Indonesia".</p>}
              <div className="space-y-2" data-testid="addr-list">
                {addrs.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={a.label || ""} onChange={(e) => { const arr = [...addrs]; arr[i] = { ...a, label: e.target.value }; setS({ ...s, addresses: arr }); }} data-testid={`addr-label-${i}`} className={inputCls + " col-span-4"} placeholder="Label (mis: Gudang)" />
                    <input value={a.address || ""} onChange={(e) => { const arr = [...addrs]; arr[i] = { ...a, address: e.target.value }; setS({ ...s, addresses: arr }); }} data-testid={`addr-value-${i}`} className={inputCls + " col-span-7"} placeholder="Alamat lengkap" />
                    <button type="button" onClick={() => setS({ ...s, addresses: addrs.filter((_, x) => x !== i) })} data-testid={`addr-remove-${i}`} className="col-span-1 text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>Rekening Bank Bisnis (bisa lebih dari 1)</label>
                <button type="button" onClick={() => setS({ ...s, bank_accounts: [...banks, { bank_name: "", account_number: "", holder: "" }] })} data-testid="bank-add" className="inline-flex items-center gap-1 text-xs font-bold text-[#C9A227] hover:underline"><Plus className="h-3.5 w-3.5" /> Tambah Rekening</button>
              </div>
              {banks.length === 0 && <p className="text-xs text-slate-400">Belum ada daftar — sementara memakai rekening tunggal dari menu Pengaturan.</p>}
              <div className="space-y-2" data-testid="bank-list">
                {banks.map((b, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={b.bank_name || ""} onChange={(e) => { const a = [...banks]; a[i] = { ...b, bank_name: e.target.value }; setS({ ...s, bank_accounts: a }); }} data-testid={`bank-name-${i}`} className={inputCls + " col-span-3"} placeholder="Bank (BCA/BRI)" />
                    <input value={b.account_number || ""} onChange={(e) => { const a = [...banks]; a[i] = { ...b, account_number: e.target.value }; setS({ ...s, bank_accounts: a }); }} data-testid={`bank-number-${i}`} className={inputCls + " col-span-4"} placeholder="No. rekening" />
                    <input value={b.holder || ""} onChange={(e) => { const a = [...banks]; a[i] = { ...b, holder: e.target.value }; setS({ ...s, bank_accounts: a }); }} data-testid={`bank-holder-${i}`} className={inputCls + " col-span-4"} placeholder="Atas nama" />
                    <button type="button" onClick={() => setS({ ...s, bank_accounts: banks.filter((_, x) => x !== i) })} data-testid={`bank-remove-${i}`} className="col-span-1 text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <ImgField label="Gambar QRIS (untuk pembayaran QRIS)" value={s.qris_image || ""} onChange={(v) => setS({ ...s, qris_image: v })} testid="site-qris" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
            <h2 className="font-bold">Bagian Hero (Paling Atas)</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Label Kecil</label>
                <input value={hc.hero_label || ""} onChange={(e) => setHc("hero_label", e.target.value)} data-testid="hc-hero-label" className={inputCls} placeholder="Supplier & Distributor Tunggal Resmi — Indonesia" />
              </div>
              <div>
                <label className={labelCls}>Judul Utama</label>
                <input value={hc.hero_title || ""} onChange={(e) => setHc("hero_title", e.target.value)} data-testid="hc-hero-title" className={inputCls} placeholder="Tali Tambang, Katrol & Timbangan" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Kata Penekanan (emas, miring)</label>
              <input value={hc.hero_highlight || ""} onChange={(e) => setHc("hero_highlight", e.target.value)} data-testid="hc-hero-highlight" className={inputCls} placeholder="Berkualitas Sempurna" />
            </div>
            <div>
              <label className={labelCls}>Deskripsi Hero</label>
              <textarea rows={2} value={hc.hero_desc || ""} onChange={(e) => setHc("hero_desc", e.target.value)} data-testid="hc-hero-desc" className={inputCls} placeholder="Deskripsi singkat di bawah judul..." />
            </div>
            <div className="border-t border-slate-100 pt-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="font-bold">Slider Gambar &amp; Video Hero</h3>
                  <p className="text-xs text-slate-400">Semua gambar dan video tampil 12 detik secara default. Durasi setiap slide dapat diatur.</p>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setHeroSlides([...heroSlides, { type: "image", url: "", alt: "", duration_seconds: 12 }])} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-bold"><Plus className="h-3.5 w-3.5" /> Gambar</button>
                  <button type="button" onClick={() => setHeroSlides([...heroSlides, { type: "video", url: "", alt: "", duration_seconds: 12 }])} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-xs font-bold"><Plus className="h-3.5 w-3.5" /> Video</button>
                </div>
              </div>
              <div className="space-y-3">
                {heroSlides.map((slide, index) => (
                  <HeroSlideField
                    key={`${slide.type}-${index}`}
                    slide={slide}
                    index={index}
                    isFirst={index === 0}
                    isLast={index === heroSlides.length - 1}
                    onChange={(value) => { const items = [...heroSlides]; items[index] = value; setHeroSlides(items); }}
                    onRemove={() => setHeroSlides(heroSlides.filter((_, itemIndex) => itemIndex !== index))}
                    onMove={(direction) => { const target = index + direction; if (target < 0 || target >= heroSlides.length) return; const items = [...heroSlides]; [items[index], items[target]] = [items[target], items[index]]; setHeroSlides(items); }}
                  />
                ))}
                {!heroSlides.length && <p className="rounded-md border border-dashed border-slate-300 p-5 text-center text-xs text-slate-400">Belum ada slide khusus. Website masih memakai gambar hero bawaan.</p>}
              </div>
              <ImageRatioPicker value={hc.hero_image_ratio} onChange={(value) => setHc("hero_image_ratio", value)} defaultRatio="4:3" testid="hc-hero-slider-ratio" />
            </div>
          </div>

          <div className="space-y-5 rounded-md border border-slate-200 bg-white p-6">
            <div>
              <h2 className="font-bold">Galeri Video di Beranda</h2>
              <p className="mt-1 text-xs text-slate-400">Atur jumlah kartu video dalam setiap slide dan banyaknya slide yang tampil. Video lainnya tetap tersedia melalui tombol Lihat Semua.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Video per slide</label>
                <select
                  value={hc.video_items_per_slide ?? 2}
                  onChange={(event) => setHc("video_items_per_slide", Number(event.target.value))}
                  className={inputCls}
                  data-testid="home-video-items-per-slide"
                >
                  <option value={1}>1 video</option>
                  <option value={2}>2 video</option>
                  <option value={3}>3 video</option>
                  <option value={4}>4 video</option>
                </select>
                <p className="mt-1 text-[10px] text-slate-400">Rekomendasi: 2 video agar judul dan deskripsi tetap nyaman dibaca.</p>
              </div>
              <div>
                <label className={labelCls}>Jumlah slide di beranda</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="1"
                  value={hc.video_slide_count ?? 2}
                  onChange={(event) => setHc("video_slide_count", Math.min(10, Math.max(1, Number(event.target.value) || 1)))}
                  className={inputCls}
                  data-testid="home-video-slide-count"
                />
                <p className="mt-1 text-[10px] text-slate-400">Bisa diatur 1–10 slide. Slide kosong otomatis tidak ditampilkan.</p>
              </div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
              Kapasitas beranda saat ini: maksimal <strong>{(Number(hc.video_items_per_slide) || 2) * (Number(hc.video_slide_count) || 2)} video</strong>. Urutan video mengikuti angka Urutan Tampil pada menu Kelola Video.
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-bold">Bagian Kategori Produk</h2>
              <button type="button" onClick={() => setHc("categories", [...cats, { key: `kategori-${Date.now()}`, title: "", desc: "", image: "", image_ratio: "4:3" }])} data-testid="hc-cat-add" className="inline-flex items-center gap-1 rounded-md border border-[#C9A227] px-3 py-2 text-xs font-bold text-[#9A7A15]"><Plus className="h-3.5 w-3.5" /> Tambah Kategori</button>
            </div>
            {cats.map((c, i) => (
              <div key={c.key} className="border border-slate-100 rounded-md p-4 space-y-3" data-testid={`hc-cat-${c.key}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Kategori {i + 1}</p>
                  <button type="button" onClick={() => setHc("categories", cats.filter((_, itemIndex) => itemIndex !== i))} data-testid={`hc-cat-remove-${i}`} className="inline-flex items-center gap-1 text-xs font-bold text-red-500"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={c.title} onChange={(e) => { const arr = [...cats]; arr[i] = { ...c, title: e.target.value }; setHc("categories", arr); }} data-testid={`hc-cat-title-${i}`} className={inputCls} placeholder="Judul kategori" />
                  <input value={c.desc} onChange={(e) => { const arr = [...cats]; arr[i] = { ...c, desc: e.target.value }; setHc("categories", arr); }} data-testid={`hc-cat-desc-${i}`} className={inputCls} placeholder="Deskripsi singkat" />
                </div>
                <ImgField
                  label="Gambar (kosongkan = otomatis dari foto produk)"
                  value={c.image}
                  onChange={(v) => { const arr = [...cats]; arr[i] = { ...c, image: v }; setHc("categories", arr); }}
                  ratio={c.image_ratio}
                  onRatioChange={(v) => { const arr = [...cats]; arr[i] = { ...c, image_ratio: v }; setHc("categories", arr); }}
                  defaultRatio="4:3"
                  testid={`hc-cat-image-${i}`}
                />
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-bold">Bagian Merek Resmi</h2>
              <button type="button" onClick={() => setHc("brands", [...brands, { id: `merek-${Date.now()}`, name: "", desc: "", image: "", image_ratio: "16:9" }])} data-testid="hc-brand-add" className="inline-flex items-center gap-1 rounded-md border border-[#C9A227] px-3 py-2 text-xs font-bold text-[#9A7A15]"><Plus className="h-3.5 w-3.5" /> Tambah Merek</button>
            </div>
            {brands.map((b, i) => (
              <div key={b.id || i} className="border border-slate-100 rounded-md p-4 space-y-3" data-testid={`hc-brand-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Merek {i + 1}</p>
                  <button type="button" onClick={() => setHc("brands", brands.filter((_, itemIndex) => itemIndex !== i))} data-testid={`hc-brand-remove-${i}`} className="inline-flex items-center gap-1 text-xs font-bold text-red-500"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input value={b.name} onChange={(e) => { const arr = [...brands]; arr[i] = { ...b, name: e.target.value }; setHc("brands", arr); }} data-testid={`hc-brand-name-${i}`} className={inputCls} placeholder="Nama merek" />
                  <input value={b.desc} onChange={(e) => { const arr = [...brands]; arr[i] = { ...b, desc: e.target.value }; setHc("brands", arr); }} data-testid={`hc-brand-desc-${i}`} className={inputCls} placeholder="Deskripsi merek" />
                </div>
                <ImgField
                  label="Gambar Merek"
                  value={b.image}
                  onChange={(v) => { const arr = [...brands]; arr[i] = { ...b, image: v }; setHc("brands", arr); }}
                  ratio={b.image_ratio}
                  onRatioChange={(v) => { const arr = [...brands]; arr[i] = { ...b, image_ratio: v }; setHc("brands", arr); }}
                  defaultRatio="16:9"
                  testid={`hc-brand-image-${i}`}
                />
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5">
            <h2 className="font-bold">Bagian Keunggulan (4 Kartu)</h2>
            {feats.map((f, i) => (
              <div key={i} className="border border-slate-100 rounded-md p-4 space-y-3" data-testid={`hc-feature-${i}`}>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Kartu {i + 1}</p>
                <input value={f.title} onChange={(e) => { const arr = [...feats]; arr[i] = { ...f, title: e.target.value }; setHc("features", arr); }} data-testid={`hc-feature-title-${i}`} className={inputCls} placeholder="Judul keunggulan" />
                <textarea rows={2} value={f.desc} onChange={(e) => { const arr = [...feats]; arr[i] = { ...f, desc: e.target.value }; setHc("features", arr); }} data-testid={`hc-feature-desc-${i}`} className={inputCls} placeholder="Deskripsi singkat" />
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
            <h2 className="font-bold">Bagian Ajakan (CTA Bawah)</h2>
            <div>
              <label className={labelCls}>Judul CTA</label>
              <input value={hc.cta_title || ""} onChange={(e) => setHc("cta_title", e.target.value)} data-testid="hc-cta-title" className={inputCls} placeholder="Siap Berbelanja atau Bertanya Dulu?" />
            </div>
            <div>
              <label className={labelCls}>Deskripsi CTA</label>
              <textarea rows={2} value={hc.cta_subtitle || ""} onChange={(e) => setHc("cta_subtitle", e.target.value)} data-testid="hc-cta-subtitle" className={inputCls} placeholder="Teks pendukung CTA..." />
            </div>
          </div>
        </div>
      )}

      {tab === "layout" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold mb-1">Urutan & Visibilitas Section Beranda</h2>
            <p className="text-xs text-slate-400 mb-5">Atur urutan tampilan section di halaman utama dan sembunyikan yang tidak diperlukan. Hero (bagian atas) selalu tampil paling pertama.</p>
            <div className="space-y-2" data-testid="layout-list">
              {layout.map((sec, i) => (
                <div key={sec.key} data-testid={`layout-${sec.key}`} className={`flex items-center justify-between border rounded-md px-4 py-3 ${sec.visible === false ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-300"}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-accent text-xs text-slate-400 w-6">{i + 1}</span>
                    <span className="text-sm font-semibold">{sec.label}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`layout-up-${sec.key}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A] disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === layout.length - 1} data-testid={`layout-down-${sec.key}`} className="p-1.5 text-slate-400 hover:text-[#0B1F3A] disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                    <button
                      onClick={() => { const arr = [...layout]; arr[i] = { ...sec, visible: sec.visible === false }; setLayout(arr); }}
                      data-testid={`layout-toggle-${sec.key}`}
                      className={`ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${sec.visible === false ? "bg-slate-200 text-slate-500" : "bg-emerald-100 text-emerald-700"}`}
                    >
                      {sec.visible === false ? <><EyeOff className="h-3.5 w-3.5" /> Tersembunyi</> : <><Eye className="h-3.5 w-3.5" /> Tampil</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-5 text-sm text-amber-800">
            <strong>Urutan produk</strong> diatur per produk di menu <strong>Produk</strong> melalui kolom "Urutan" — angka lebih kecil tampil lebih dulu di toko & katalog.
          </div>
        </div>
      )}

      {tab === "desain" && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold mb-1">Layout Halaman Utama</h2>
            <p className="text-xs text-slate-400 mb-5">8 pilihan tata letak beranda — mengubah gaya hero dan urutan bagian. Memilih layout akan mereset urutan kustom di tab Tata Letak.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="layout-grid">
              {LAYOUTS.map((l) => (
                <button key={l.v} onClick={() => setS({ ...s, home_layout: l.v, home_sections: [] })} data-testid={`layout-opt-${l.v}`} className={`text-left border rounded-md p-4 transition-colors ${(s.home_layout || "classic") === l.v ? "border-[#C9A227] bg-amber-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <p className="font-bold text-sm">{l.l}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{l.d}</p>
                  {(s.home_layout || "classic") === l.v && <p className="text-[10px] font-bold text-[#C9A227] mt-2 uppercase tracking-widest">Terpilih</p>}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold mb-1">Tata Letak Daftar Blog</h2>
            <p className="text-xs text-slate-400 mb-4">Mengubah tampilan halaman /blog.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="bloglayout-grid">
              {BLOG_LAYOUTS.map((l) => (
                <button key={l.v} onClick={() => setS({ ...s, blog_layout: l.v })} data-testid={`bloglist-${l.v}`} className={`text-left border rounded-md p-4 transition-colors ${(s.blog_layout || "grid") === l.v ? "border-[#C9A227] bg-amber-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <p className="font-bold text-sm">{l.l}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{l.d}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-6">
            <h2 className="font-bold mb-1">Tata Letak Halaman Artikel</h2>
            <p className="text-xs text-slate-400 mb-4">Mengubah tampilan halaman detail artikel blog.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="blogdetaillayout-grid">
              {BLOG_DETAIL_LAYOUTS.map((l) => (
                <button key={l.v} onClick={() => setS({ ...s, blog_detail_layout: l.v })} data-testid={`blogdetail-${l.v}`} className={`text-left border rounded-md p-4 transition-colors ${(s.blog_detail_layout || "klasik") === l.v ? "border-[#C9A227] bg-amber-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <p className="font-bold text-sm">{l.l}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{l.d}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
